// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { Box, useTheme } from '@mui/system';
import { Button, Typography, Select, MenuItem, InputBase, IconButton, Menu, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import { useDraggable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppDispatch, useAppSelector } from 'Hooks';
import SearchField from 'Components/SearchField';
import OverflowTooltipText from 'Components/OverflowTooltipText';
import ProjectPreview from 'Assets/projectPreview.png';
import { PROJECTS_SECTION_TITLE } from 'Utils/constants';
import { resolveDeliverySchedule } from 'Utils/deliverySchedule';
import { formatProjectDate, PROJECT_VALUE_UNSET } from 'Utils/projectDisplay';
import { isPrimaryPress } from 'Utils/pointerPress';
import { GetProjectDetailsRoute, GetProjectEsri3DMapRoute } from 'Utils/routes';
import { getStatusChipWithPhase } from 'Features/map/EsriDashboardMap/ProjectOverviewCard/statusChip';
import FolderBreadcrumb from 'Components/FolderBreadcrumb';
import ProjectFolderBadge from 'Components/ProjectFolderBadge';
import ProjectReferenceLine from 'Components/ProjectReferenceLine';
import FolderListItem from 'Features/folderList/FolderListItem';
import { findFolderById, selectFolderList } from 'Features/folderList/folderListSelector';
import {
  MarkOrderSortFilterTouchedAction, SetKeywordFilterAction, SetMapFocusAction, SetMapFolderAction,
  SetOrderSortFilterAction, SetSelectedPinProjectAction, SetSortFilterAction,
} from '../projectListActions';
import { GetMapCoordinatesThunk, ReadThumbnailThunk } from '../projectListThunk';
import { hasLocation, matchesFolderKeyword } from '../filterProjects';
import { useMountedInChunks } from '../useMountedInChunks';
import { useMapFolderBadgeName } from '../useMapFolderBadgeName';
import { useMapViewToggle } from '../useMapViewToggle';
import ProjectsMapToggle from '../ProjectsMapToggle';
import { useRailScrollPosition } from './useRailScrollPosition';
import { selectEffectiveOrderSortFilter, selectScopedCoordinates } from '../projectListSelector';
import { sortProjects } from '../sortProjects';
import { FOLDER_ORDER_SORT_FILTER_OPTIONS, ORDER_SORT_FILTER_OPTIONS, PROJECT_SORT_OPTIONS } from '../projectViewOptions';
import stylesDef from './styles';

type RailStyles = ReturnType<typeof stylesDef>;

// Cards mounted per step: the first chunk renders at once, the rest as the user scrolls.
const CARD_CHUNK = 30;

// memo: the rail re-renders on every projectList slice change (selection, thumbnail
// resolutions streaming in), and re-rendering every card for each of those reads as the
// whole list regenerating. A card re-renders only when its own inputs change.
const ProjectMapCard = memo(({ project, folderName, styles, isSelected, onSelect }: {
  project: IProjectMapCoordinate;
  // Resolved by the rail (useMapFolderBadgeName); undefined when there is nothing to badge.
  folderName: string | undefined;
  styles: RailStyles;
  isSelected: boolean;
  onSelect: (selected: IProjectMapCoordinate) => void;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const status = getStatusChipWithPhase(project);
  // The shared schedule owns the "does this project have a live delivery" rule (canceled
  // orders have none) - the rail and the pin card must never disagree on it.
  const hasDeliverySchedule = resolveDeliverySchedule(project).state !== 'none';
  const hasThumbnail = Boolean(project.thumbnail && !project.thumbnail.includes('undefined'));
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Raw S3 thumbnail URLs 403 against the private bucket; the grid resolves each card's image
  // through readThumbnail, so the rail does the same - lazily, once a card scrolls into view,
  // and once per session: a resolved thumbnail is kept in the store, and re-resolving would
  // mint a fresh signed URL and visibly reload an image the user already has.
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true });
  useEffect(() => {
    if (inView && hasThumbnail && !project.thumbnailResolved) dispatch(ReadThumbnailThunk(project._id));
  }, [inView, project.thumbnailResolved]);

  // A drag source, as the grid's cards are: dropped on one of the rail's folder cards, the
  // project is filed into that folder (AUT-6509). The card itself follows the pointer.
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: project._id,
    data: { type: 'project' },
  });
  // One node, two refs: the viewport observer that defers the thumbnail, and the drag source.
  const setCardRef = useCallback((node: HTMLElement | null) => {
    inViewRef(node);
    setNodeRef(node);
  }, [inViewRef, setNodeRef]);

  // A main-button press arms the drag sensor; a press that never travels its activation
  // distance is a click, and the card's onClick selects it. No click-vs-drag test of its own:
  // once a drag activates, dnd-kit swallows the click the browser fires on its release, so a
  // filed card is never also selected. Its menu stops its own presses (below).
  const handlePointerDown = (event: React.PointerEvent) => {
    if (!isPrimaryPress(event)) return;
    listeners?.onPointerDown?.(event);
  };

  // A div with button semantics rather than a native button: Safari renders complex children
  // inside styled buttons unreliably (present in the DOM, painted at zero size). Of dnd-kit's
  // attributes only the role description is taken: its describedby text promises a keyboard
  // drag no sensor provides, and its pressed state would make the card a toggle button.
  return (
    <Box
      ref={setCardRef}
      data-project-id={project._id}
      role="button"
      tabIndex={0}
      aria-roledescription={attributes['aria-roledescription']}
      sx={[styles.card, { transform: CSS.Translate.toString(transform) }, Boolean(transform) && styles.cardDragging, isSelected && styles.cardSelected]}
      onPointerDown={handlePointerDown}
      onClick={() => onSelect(project)}
      onKeyDown={(event: React.KeyboardEvent) => {
        // Only when the card itself is focused - the "..." menu button handles its own keys.
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) onSelect(project);
      }}
    >
      <Box sx={styles.cardBody}>
        <Box
          component="img"
          src={project.thumbnailResolved ? project.thumbnail : ProjectPreview}
          alt=""
          sx={styles.cardThumb}
          onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
            // An expired signed URL still degrades to the placeholder, not a broken image.
            event.currentTarget.src = ProjectPreview;
          }}
        />
        <Box sx={styles.textCol}>
          <Box sx={styles.nameRow}>
            <OverflowTooltipText text={project.name || 'Untitled project'} sx={styles.cardName} />
            <IconButton
              size="small"
              aria-label="Project actions"
              className="railCardMenu"
              sx={[styles.cardMenuButton, isSelected && styles.cardMenuButtonVisible]}
              // Its presses stay its own: reaching the card they would arm the drag sensor, and
              // a wobble on the button would drag the card and swallow the click meant for it.
              onPointerDown={(event) => event.stopPropagation()}
              onPointerUp={(event) => event.stopPropagation()}
              onClick={(event) => {
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
              }}
            >
              <MoreHorizIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              onClick={(event) => event.stopPropagation()}
            >
              {/* Links so middle-click / cmd-click open a new tab natively. The explicit close
                  matters exactly then: a new tab leaves this one open, and an unclosed Menu
                  keeps its modal backdrop blocking the page. */}
              <MenuItem component={Link} to={GetProjectDetailsRoute(project._id)} onClick={() => setMenuAnchor(null)}>Project Details</MenuItem>
              <MenuItem component={Link} to={GetProjectEsri3DMapRoute(project._id)} onClick={() => setMenuAnchor(null)}>Open in Viewer</MenuItem>
            </Menu>
          </Box>
          <Typography sx={styles.cardDates}>
            <b>Due Date:</b>
            {` ${hasDeliverySchedule ? formatProjectDate(project.deliveryDate) : PROJECT_VALUE_UNSET}`}
          </Typography>
          {/* The card's bottom-right corner: whether the project can be mapped at all, then
              the folder it sits in. Both qualify the project rather than name it, so they sit
              away from the title, directly above the footer's rule. */}
          <Box sx={styles.cornerMarks}>
            {!hasLocation(project) && (
              <Tooltip title="No location to map" arrow placement="top">
                <LocationOffIcon sx={styles.noLocationIcon} aria-label="No location to map" />
              </Tooltip>
            )}
            {folderName && (
              <Box sx={styles.cornerFolderBadge}>
                <ProjectFolderBadge folderName={folderName} variant="inline" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box sx={styles.cardFooter}>
        <ProjectReferenceLine projectId={project._id} referenceId={project.referenceId} createdAt={project.createdAt} sx={styles.cardRef} />
        <Box sx={styles.footerStatus}>
          <Box sx={styles.statusDot(status.kind)} />
          <Typography sx={styles.statusLabel}>{status.label}</Typography>
        </Box>
      </Box>
    </Box>
  );
});

// The Figma's map-view left rail: search and the sort/filter dropdowns live here (not in a
// full-width toolbar), above a scrolling card list of exactly the projects the map plots.
// Clicking a card flies the map to that project's pin and opens its overview card. The rail sits
// in the dashboard's drag context, so its folder cards reorder and take dropped project cards
// exactly as the grid's do.
export const MapRail = () => {
  const theme = useTheme();
  const styles = useMemo(() => stylesDef(theme), [theme]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  // Individual field subscriptions: selecting the whole slice re-rendered the rail on every
  // slice change, including the hidden grid's per-keystroke fetch lifecycle.
  // The complete set narrowed to the folder the map is scoped to, if any (AUT-6432).
  const mapCoordinates = useAppSelector(selectScopedCoordinates);
  const mapFolderId = useAppSelector((state) => state.projectList.mapFolderId);
  const folderList = useAppSelector(selectFolderList);
  const mapCoordinatesLoading = useAppSelector((state) => state.projectList.mapCoordinatesLoading);
  const mapCoordinatesFailed = useAppSelector((state) => state.projectList.mapCoordinatesFailed);
  const keywordFilter = useAppSelector((state) => state.projectList.keywordFilter);
  const sortFilter = useAppSelector((state) => state.projectList.sortFilter);
  const selectedPinProjectId = useAppSelector((state) => state.projectList.selectedPinProjectId);
  const effectiveOrderSortFilter = useAppSelector(selectEffectiveOrderSortFilter);

  const projects = useMemo(() => sortProjects(mapCoordinates, sortFilter, keywordFilter), [mapCoordinates, sortFilter, keywordFilter]);

  const cardListRef = useRef<HTMLDivElement | null>(null);
  // A large org's full portfolio in one commit (cards, observers, menus) blocks the main thread
  // for seconds while the map initialises, so the cards mount in chunks; a return from a
  // project page starts at the depth the user had reached.
  const railView = useAppSelector((state) => state.projectList.railView);
  const mounted = useMountedInChunks({ itemCount: projects.length, chunk: CARD_CHUNK, initialCount: railView?.mountedCount });
  const { markSelectionFromRail } = useRailScrollPosition({ listRef: cardListRef, projects, mounted });

  // Stable so memoized cards don't re-render just because the rail did.
  const selectProjectCard = useCallback((project: IProjectMapCoordinate) => {
    // A located project flies the map to its pin and opens the overview card; an unlocated
    // one has no pin, so its card goes straight to the project's details page - still marked
    // selected, so returning from that page centers and highlights the card the user left by.
    if (!hasLocation(project)) {
      markSelectionFromRail();
      dispatch(SetSelectedPinProjectAction(project._id));
      navigate(GetProjectDetailsRoute(project._id));
      return;
    }
    markSelectionFromRail();
    dispatch(SetSelectedPinProjectAction(project._id));
    dispatch(SetMapFocusAction({ kind: 'project', id: project._id }));
  }, []);

  // Clicking a folder card enters it: the rail swaps its folder list for a breadcrumb and that
  // folder's projects, and the map plots only those (the set is complete, so this is a
  // client-side narrowing). The breadcrumb's "All Projects" leaves. A selection made outside
  // the new scope would be a card the map no longer plots, so entering or leaving drops it.
  const enterFolder = (folder: IFolder) => {
    dispatch(SetSelectedPinProjectAction(null));
    dispatch(SetMapFolderAction(folder._id));
  };
  const leaveFolder = () => {
    dispatch(SetSelectedPinProjectAction(null));
    dispatch(SetMapFolderAction(null));
  };
  const openFolder = mapFolderId ? findFolderById(folderList, mapFolderId) : undefined;
  const resolveFolderBadgeName = useMapFolderBadgeName();
  // The rail's Projects / Map switch is the way out of the map (inside a folder it lands on
  // that folder's page).
  const { showProjects } = useMapViewToggle();
  // Searching narrows folders by name, by the rule the dashboard's Folders section applies.
  const visibleFolders = useMemo(() => (keywordFilter
    ? folderList.filter((folder: IFolder) => matchesFolderKeyword(folder, keywordFilter))
    : folderList), [folderList, keywordFilter]);

  return (
    <Box sx={styles.root}>
      {mapFolderId && (
        <FolderBreadcrumb onAllProjectsClick={leaveFolder}>
          {openFolder?.name || 'Folder'}
        </FolderBreadcrumb>
      )}
      {/* The rail and the grid drive one set, so these controls only set state: the search and
          filter refetch through useDashboardProjects, and sort re-orders what is loaded. */}
      <Box sx={styles.searchRow}>
        <SearchField
          placeholder="Search by projects and folders"
          value={keywordFilter}
          sx={styles.searchField}
          onChange={(value: string) => {
            dispatch(SetKeywordFilterAction(value));
          }}
        />
        <ProjectsMapToggle
          value="map"
          onChange={(view) => {
            if (view === 'projects') showProjects();
          }}
        />
      </Box>
      <Box ref={cardListRef} sx={styles.cardList}>
        {/* Folders scroll with the cards, ahead of them; a click scopes the map to the folder
            rather than leaving for its page. Hidden once inside one. Dragging a folder card
            reorders the folders as on the dashboard, locked while searching for the same reason
            (a filtered subset's positions would not line up with the saved order), and a
            project card dropped on one is filed into it (AUT-6509). */}
        {!mapFolderId && visibleFolders.length > 0 && (
          <Box sx={styles.folderSection}>
            <Typography sx={styles.foldersHeading}>Folders</Typography>
            <SortableContext items={visibleFolders.map((folder: IFolder) => folder._id)} strategy={verticalListSortingStrategy}>
              {visibleFolders.map((folder: IFolder) => (
                <FolderListItem key={folder._id} folder={folder} showMapview reorderable={!keywordFilter} onSelect={enterFolder} isSelected={false} />
              ))}
            </SortableContext>
          </Box>
        )}
        {/* The section heading and its controls sit under the folders and above the cards - the
            order the dashboard reads in - and pin to the top of the list once the folders have
            scrolled away, so sorting never needs a scroll back up. */}
        <Box sx={styles.headerRow}>
          <Typography sx={styles.headerTitle}>{PROJECTS_SECTION_TITLE[effectiveOrderSortFilter]}</Typography>
          <Select
            inputProps={{ name: 'sort', id: 'map-rail-sort' }}
            input={<InputBase />}
            sx={styles.headerSelect}
            value={sortFilter}
            onChange={(event) => {
              dispatch(SetSortFilterAction(event.target.value));
            }}
          >
            {PROJECT_SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
          {/* Bound to the effective value, not the stored one: inside a folder an untouched or
              Unassigned filter reads as All Projects, which is what the options offer
              there - the stored value would not be among them and the select would show nothing. */}
          <Select
            inputProps={{ name: 'filterby', id: 'map-rail-filterby' }}
            input={<InputBase />}
            sx={styles.headerSelect}
            value={effectiveOrderSortFilter}
            onOpen={() => dispatch(MarkOrderSortFilterTouchedAction())}
            onChange={(event) => {
              dispatch(MarkOrderSortFilterTouchedAction());
              dispatch(SetOrderSortFilterAction(event.target.value as ProjectOrderSortFilter));
            }}
          >
            {/* Inside a folder every project is in it, so the Unassigned filter is left out - it
                would select nothing. */}
            {(mapFolderId ? FOLDER_ORDER_SORT_FILTER_OPTIONS : ORDER_SORT_FILTER_OPTIONS).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {projects.slice(0, mounted.mountedCount).map((project) => (
          <ProjectMapCard
            key={project._id}
            project={project}
            folderName={resolveFolderBadgeName(project.folderId)}
            styles={styles}
            isSelected={project._id === selectedPinProjectId}
            onSelect={selectProjectCard}
          />
        ))}
        {mounted.mountedCount < projects.length && <Box ref={mounted.sentinelRef} />}
        {/* A failed fetch says so, with a way back, rather than reading as an empty account. */}
        {!mapCoordinatesLoading && mapCoordinatesFailed && (
          <Box sx={styles.emptyState}>
            <Typography variant="body1">We couldn’t load your projects.</Typography>
            <Button variant="outlined" sx={styles.retryButton} onClick={() => dispatch(GetMapCoordinatesThunk())}>
              Try again
            </Button>
          </Box>
        )}
        {!mapCoordinatesLoading && !mapCoordinatesFailed && projects.length === 0 && (
          <Box sx={styles.emptyState}>
            <Typography variant="body1">No projects to show</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MapRail;
